import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useSiteLogo } from "@/hooks/useSiteLogo";

const menuItems = [
  { path: "/admin/members", label: "회원관리", icon: "ri-team-line" },
  { path: "/admin/posts", label: "게시글관리", icon: "ri-article-line" },
  { path: "/admin/notices", label: "공지사항관리", icon: "ri-megaphone-line" },
  { path: "/admin/banners", label: "배너관리", icon: "ri-image-line" },
  { path: "/admin/verified", label: "보증업체관리", icon: "ri-shield-check-line" },
  { path: "/admin/points", label: "포인트관리", icon: "ri-exchange-funds-line" },
  { path: "/admin/gifts", label: "기프티콘교환관리", icon: "ri-gift-2-line" },
  { path: "/admin/logo", label: "로고관리", icon: "ri-image-edit-line" },
  { path: "/admin/settings", label: "사이트설정", icon: "ri-settings-3-line" },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logoUrl } = useSiteLogo();

  const checkAdmin = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setAdminEmail(user.email || "");

      const { data: profile } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .maybeSingle();

      if (profile?.role !== "admin") {
        setIsAdmin(false);
      } else {
        setIsAdmin(true);
      }
    } catch {
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAdmin();
  }, [checkAdmin]);

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    navigate("/", { replace: true });
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <i className="ri-shield-keyhole-line text-3xl text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-foreground-800 mb-2">접근 권한이 없습니다</h1>
          <p className="text-sm text-foreground-600 mb-6">관리자만 접근할 수 있습니다.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
          >
            <i className="ri-home-4-line" />
            홈으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-60 bg-background-100 border-r border-background-300 flex flex-col z-50 transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-background-300">
          <Link to="/" className="flex items-center gap-2" onClick={() => setSidebarOpen(false)}>
            <img
              alt="월드컵티비 시즌2"
              className="w-8 h-8 rounded-lg"
              src={logoUrl}
            />
            <span className="text-sm font-black tracking-tight">
              <span className="text-primary-500">월드컵티비</span>
              <span className="text-foreground-700"> 시즌2</span>
            </span>
          </Link>
          <p className="text-[10px] text-foreground-600 mt-1.5 px-1">관리자 콘솔</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-primary-500/10 text-primary-500 border border-primary-500/20"
                    : "text-foreground-600 hover:text-foreground-800 hover:bg-background-200/80"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={item.icon} />
                </span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="px-3 py-4 border-t border-background-300 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-600 hover:text-foreground-800 hover:bg-background-200/80 transition-colors whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            사이트로 돌아가기
          </Link>

          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-red-400 hover:bg-red-500/10 transition-colors w-full text-left whitespace-nowrap cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-logout-box-line" />
            </span>
            로그아웃
          </button>

          <div className="px-3 pt-2">
            <span className="text-[10px] text-foreground-600 bg-background-200 px-2 py-1 rounded-full">
              {adminEmail}
            </span>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-background-100 border-b border-background-300 px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-menu-line text-lg text-foreground-600" />
            </button>
            <h1 className="text-sm font-bold text-foreground-800">
              {menuItems.find((m) => m.path === location.pathname)?.label || "관리자"}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[10px] text-foreground-600 bg-background-200 px-2.5 py-1 rounded-full hidden sm:inline-block">
              <span className="w-3 h-3 inline-flex items-center justify-center mr-1">
                <i className="ri-admin-line text-accent-500" />
              </span>
              {adminEmail}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}